import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Avatar,
  IconButton,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import RemoveIcon from "@mui/icons-material/Remove";
import { useDraggable } from "@dnd-kit/core";

import {
  COLORS,
  PRIORITY_MAP,
  RADIUS,
  deptColorOf,
  departmentTagStyle,
  formatDueDate,
  initialsOf,
} from "./ticketTheme";

function Tag({ children, color, bg, fontSize = 11 }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        bgcolor: bg,
        color,
        fontSize,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        borderRadius: `${RADIUS.field}px`,
        px: 1,
        py: 0.375,
        lineHeight: 1,
        whiteSpace: "nowrap",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {children}
    </Box>
  );
}

export default function TicketCard({
  ticket,
  status,
  departments,
  onClick,
  onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: ticket.id,
      data: { status },
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 10,
      }
    : undefined;

  const priority = PRIORITY_MAP[ticket.priority] || PRIORITY_MAP.medium;
  const due = formatDueDate(ticket.due_date);

  // The board API (tickets/services/ticket_service.py) returns flat
  // department_id/department_name and assignee_id/assignee_name fields —
  // there is no nested ticket.department / ticket.assignee object. Reading
  // ticket.department?.name here silently returns undefined for every
  // ticket, which is why department tags and counts were broken before.
  const departmentName = ticket.department_name;
  const assigneeName = ticket.assignee_name;

  // Departments each carry their own color — SideBar.jsx (and everywhere
  // else in the app) actually reads it off dept.metadata.bg, with
  // dept.color as an alternate/legacy field, so deptColorOf() checks both.
  // This is what lights up the card's background tint and its department
  // tag with that department's real color instead of a fixed purple.
  const department = (departments || []).find(
    (d) => String(d.id) === String(ticket.department_id)
  );
  const departmentColor = department ? deptColorOf(department) : null;
  // Same light tint used for the department tag's own background, just
  // reused here to fill the whole card instead of a saturated accent bar.
  const departmentCardBg = departmentTagStyle(departmentColor).bg;

  // dnd-kit's PointerSensor already has an activation distance, so a plain
  // click (no drag) still reaches onClick cleanly — only guard against
  // firing "open" mid-drag.
  const handleClick = () => {
    if (!isDragging) onClick?.();
  };

  return (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      elevation={0}
      sx={{
        p: 3,
        cursor: isDragging ? "grabbing" : "pointer",
        opacity: isDragging ? 0.5 : 1,
        bgcolor: departmentCardBg,
        border: "1px solid",
        borderColor: COLORS.border,
        borderRadius: `${RADIUS.card}px`,
        transition:
          "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
        "&:hover": {
          boxShadow: "0 12px 24px rgba(21, 22, 26, 0.12)",
          borderColor: COLORS.borderStrong,
          transform: "translateY(-3px)",
        },
        ...style,
      }}
    >
      <Stack spacing={1.75}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
            {departmentName && (
              <Tag
                color="#fff"
                bg={departmentColor || COLORS.accent}
                fontSize={9.5}
              >
                {departmentName}
              </Tag>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <Tag color={priority.color} bg={priority.soft}>
              {priority.label}
            </Tag>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(ticket.id);
              }}
              sx={{
                width: 20,
                height: 20,
                color: COLORS.textDisabled,
                bgcolor: COLORS.pageBg,
                "&:hover": {
                  color: COLORS.danger,
                  bgcolor: COLORS.dangerSoft,
                },
              }}
            >
              <RemoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Stack>

        <Typography
          variant="body1"
          fontWeight={700}
          sx={{ color: COLORS.textPrimary, fontSize: 15 }}
        >
          {ticket.title}
        </Typography>

        {ticket.description && (
          <Typography
            variant="body2"
            sx={{
              color: COLORS.textSecondary,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {ticket.description}
          </Typography>
        )}

        {assigneeName && (
          <Typography
            variant="caption"
            sx={{ color: COLORS.textSecondary, fontWeight: 600 }}
          >
            @{assigneeName}
          </Typography>
        )}

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          pt={0.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            {ticket.comment_count > 0 && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 13, color: COLORS.textDisabled }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: COLORS.textDisabled }}
                >
                  {ticket.comment_count}
                </Typography>
              </Stack>
            )}
            {ticket.attachment_count > 0 && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <AttachFileIcon
                  sx={{ fontSize: 13, color: COLORS.textDisabled }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: COLORS.textDisabled }}
                >
                  {ticket.attachment_count}
                </Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {due && (
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  borderRadius: `${RADIUS.pill}px`,
                  px: 1,
                  py: 0.25,
                  bgcolor:
                    due.tone === "overdue" ? COLORS.dangerSoft : COLORS.pageBg,
                  color:
                    due.tone === "overdue"
                      ? COLORS.danger
                      : COLORS.textSecondary,
                }}
              >
                {due.text}
              </Box>
            )}
            {assigneeName && (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 11,
                  bgcolor: departmentColor || COLORS.accent,
                }}
              >
                {initialsOf(assigneeName)}
              </Avatar>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
